"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var typeChecks_1 = require("./typeChecks");
var intlInstanceCache_1 = require("./intlInstanceCache");
var transform_1 = require("./transform");
var collection_1 = require("./collection");
var date_1 = require("./date");
exports.padStart = function (value, length, padder) {
    if (padder === void 0) { padder = '0'; }
    if (value !== undefined && value !== null) {
        var response = value.toString();
        if (response.length < length) {
            var split = response.split('');
            while (split.length < length) {
                split.unshift(padder);
            }
            response = split.join('');
        }
        return response;
    }
    return '';
};
exports.formatNumber = function (locale, options, value, translate) {
    if (locale === void 0) { locale = 'en-GB'; }
    if (typeChecks_1.isNumber(value)) {
        try {
            var numberThresholds = options.numberThresholds, _a = options.thresholdSeparator, thresholdSeparator = _a === void 0 ? '' : _a, _b = options.forceMinimumThresholdFormatting, forceMinimumThresholdFormatting = _b === void 0 ? false : _b;
            var numberThreshold = void 0;
            if (numberThresholds) {
                var sortedNumberThresholds = collection_1.sortBy(numberThresholds, function (item) { return item.value.toString(); }, 'DESC');
                for (var _i = 0, sortedNumberThresholds_1 = sortedNumberThresholds; _i < sortedNumberThresholds_1.length; _i++) {
                    var numberGroup = sortedNumberThresholds_1[_i];
                    var thresholdValue = numberGroup.value;
                    var shouldFormat = sortedNumberThresholds.indexOf(numberGroup) === sortedNumberThresholds.length - 1 && value < thresholdValue && forceMinimumThresholdFormatting;
                    if (Math.abs(value) >= Math.abs(thresholdValue) || shouldFormat) {
                        numberThreshold = numberGroup;
                        value = (value < 0 ? -1 : 1) * Math.abs(value) / Math.abs(thresholdValue);
                        break;
                    }
                }
            }
            var formattedNumber = intlInstanceCache_1.default.getNumber(locale, options).format(value);
            if (numberThreshold) {
                var text = numberThreshold.text, _c = numberThreshold.separator, separator = _c === void 0 ? thresholdSeparator : _c, _d = numberThreshold.displayPosition, displayPosition = _d === void 0 ? 'suffix' : _d;
                var translatedText = (translate && translate(text)) || text;
                if (displayPosition === 'suffix') {
                    return "" + formattedNumber + separator + translatedText;
                }
                if (displayPosition === 'prefix') {
                    return "" + translatedText + separator + formattedNumber;
                }
            }
            return formattedNumber;
        }
        catch (e) {
            console.warn(e);
            return value;
        }
    }
    return value;
};
var getFormatSymbolData = function (format, symbolChar) {
    var formatCharacters = format.split('');
    var start;
    var length = 0;
    for (var i = 0; i < formatCharacters.length; i++) {
        var char = formatCharacters[i];
        var prevChar = i > 0 ? formatCharacters[i - 1] : null;
        if (start === undefined) {
            if (char === symbolChar) {
                start = i;
                length++;
            }
        }
        else if (prevChar === symbolChar && char === symbolChar) {
            length++;
        }
    }
    return {
        start: start,
        length: length,
        key: format.slice(start, start + length),
    };
};
var getDatePart = function (date, symbol, key, defaultPart) {
    if (date && symbol && symbol.length > 0 && symbol.key === key) {
        return date.slice(symbol.start, symbol.start + symbol.length);
    }
    return defaultPart;
};
var formatGmtOffset = function (hours) {
    if (hours === 0) {
        return 'GMT';
    }
    return "GMT" + (hours > 0 ? "+" + hours : hours);
};
/**
 * This will take the defined timeZone or fallback to the user's timeZone to provide the GMT offset
 *
 * @param [targetDateStr] string
 * @param [targetTimeZone] string (defaults to user's timezone)
 * @returns string eg. GMT+2
 */
var getGmtOffset = function (targetDateStr, targetTimeZone) {
    // If we are targetting UTC, the offset will simply be GMT.
    if (targetTimeZone === 'UTC') {
        return 'GMT';
    }
    // If we are targeting another timezone, get the difference between UTC and the target time zone
    if (targetTimeZone) {
        var utcDate = date_1.getDateAsUTC(targetDateStr);
        if (!date_1.isValidDate(utcDate)) {
            return '';
        }
        // here we get timestamps from new date objects based on locale strings using the en-US culture which the Date constructor can read back.
        var utcTime = new Date(utcDate.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();
        var tzTime = new Date(utcDate.toLocaleString('en-US', { timeZone: targetTimeZone })).getTime();
        // determine the hour offset between the two time zones.
        var diff = (utcTime - tzTime) / 3600000 * -1;
        return formatGmtOffset(diff);
    }
    // Otherwise we are targeting the user's time zone to workout the time zone difference
    if (!targetTimeZone) {
        // Keeping DST in mind we create a date based on the date string
        var targetDate = new Date(targetDateStr);
        if (!date_1.isValidDate(targetDate)) {
            return '';
        }
        var diff = targetDate.getTimezoneOffset() / 60 * -1;
        return formatGmtOffset(diff);
    }
};
var dateFromFormat = function (props) {
    var date = props.date, fromFormat = props.fromFormat, intlOptions = props.intlOptions;
    var newDate = date;
    if (date && typeof date === 'string') {
        // determine what date parts can be pulled off of the provided date string.
        var usedSymbols = {
            year: getFormatSymbolData(fromFormat, 'Y'),
            month: getFormatSymbolData(fromFormat, 'M'),
            day: getFormatSymbolData(fromFormat, 'D'),
            hour: getFormatSymbolData(fromFormat, 'H'),
            minute: getFormatSymbolData(fromFormat, 'm'),
            second: getFormatSymbolData(fromFormat, 'S'),
        };
        // Get date part values based on from format.
        var dateTimeParts = {
            year: getDatePart(date, usedSymbols.year, 'YYYY'),
            month: getDatePart(date, usedSymbols.month, 'MM'),
            day: getDatePart(date, usedSymbols.day, 'DD'),
            hour: getDatePart(date, usedSymbols.hour, 'HH', '00'),
            minute: getDatePart(date, usedSymbols.minute, 'mm', '00'),
            second: getDatePart(date, usedSymbols.second, 'SS', '00'),
        };
        // build date & time strings
        var datePart = dateTimeParts.year + "/" + dateTimeParts.month + "/" + dateTimeParts.day;
        var timePart = dateTimeParts.hour + ":" + dateTimeParts.minute + ":" + dateTimeParts.second;
        var dateStrWithNoTimeZone = datePart + " " + timePart;
        // determine time zone to use in order to provide GMT offset as a string.
        var appliedTimeZone = void 0;
        if (!intlOptions.dateFormatString && intlOptions.timeZone) {
            appliedTimeZone = intlOptions.timeZone;
        }
        var zonePart = "" + getGmtOffset(dateStrWithNoTimeZone, appliedTimeZone);
        // return date string that lines up with the target timezone so that date formatting does not shift out by a day.
        newDate = dateStrWithNoTimeZone + " " + zonePart;
    }
    return new Date(newDate);
};
/**
 * Format a provided date string based on either a string format or using intl.DateTimeFormat.
 *
 * Note: it is important that we create a date object based on the time zone we are going to format in
 * (This prevents the output date shifting by a day, because of the different between the user time zone and the target timezone).
 *
 * @param [locale='en-GB']
 * @param options
 * @param value
 * @param [translate]
 * @returns
 */
exports.formatDateTime = function (locale, options, value, translate) {
    if (locale === void 0) { locale = 'en-GB'; }
    var fromDateFormat = options.fromDateFormat || 'YYYY-MM-DD';
    // in order to prevent the Intl formatter changing the date based on the users timezone, we can default to UTC here.
    if (!options.timeZone) {
        options.timeZone = 'UTC';
    }
    var date = dateFromFormat({
        date: value,
        fromFormat: fromDateFormat,
        intlOptions: options,
    });
    if (date_1.isValidDate(date)) {
        try {
            var dateFormatString = options.dateFormatString;
            if (dateFormatString) {
                return date_1.formatDateWithStringFormat(date, dateFormatString, translate);
            }
            return intlInstanceCache_1.default.getDateTime(locale, options).format(date);
        }
        catch (e) {
            console.warn(e);
            return value;
        }
    }
    return value;
};
exports.formatter = function (format, value, translate) {
    var type = format.type, locale = format.locale, currency = format.currency, options = format.options, transforms = format.transforms;
    return spreadFormatter(type, locale, currency, options, value, transforms, translate);
};
var spreadFormatter = function (type, locale, currency, options, value, transforms, translate) {
    var _a;
    if (locale === void 0) { locale = 'en-GB'; }
    if (options === void 0) { options = {}; }
    var response = value;
    var placeholderText = options.placeholderText, _b = options.placeholderKey, placeholderKey = _b === void 0 ? 'value' : _b;
    if (transforms && typeChecks_1.isNumber(value)) {
        var typesAllowedToTransform = ['number', 'percent', 'currency'];
        if (typesAllowedToTransform.includes(type)) {
            value = transform_1.transform(value, transforms);
        }
    }
    if (type === 'string') {
        response = !typeChecks_1.isNullOrUndefined(value) && value.toString();
    }
    if (type === 'number') {
        response = exports.formatNumber(locale, options, value, translate);
    }
    if (type === 'percent') {
        var workingOptions = Object.assign({}, options || {}, { style: 'percent' });
        response = exports.formatNumber(locale, workingOptions, value, translate);
    }
    if (type === 'currency') {
        var workingOptions = Object.assign({}, options || {}, { style: 'currency' });
        workingOptions.currency = currency;
        response = exports.formatNumber(locale, workingOptions, value, translate);
    }
    if (type === 'date') {
        response = exports.formatDateTime(locale, options, value);
    }
    if (translate && placeholderText) {
        response = translate(placeholderText, (_a = {}, _a[placeholderKey] = response, _a));
    }
    return response;
};
exports.default = spreadFormatter;
//# sourceMappingURL=formatter.js.map